import React from "react";
import { Calendar, Clock, User, ArrowRight, Tag, Share2 } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";


function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="11421" className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-80 animate-pulse rounded-none bg-slate-100" data-index={i} />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} className="flex h-full flex-col overflow-hidden border-slate-200 bg-white shadow-sm transition-all hover:shadow-lg" data-index={i}>
          {post.featuredImage && (
            <div className="aspect-video w-full overflow-hidden bg-slate-100">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-700 hover:scale-110"
              />
            </div>
          )}
          <CardHeader className="p-5 pb-2">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <Calendar className="h-3 w-3" />
              <span>{post.date ? new Date(post.date).toLocaleDateString('it-IT') : "Recente"}</span>
            </div>
            <CardTitle className="line-clamp-2 text-xl font-bold leading-snug text-slate-900">
              <Link to={post.link || "#"} className="hover:text-primary transition-colors">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow p-5 pt-2">
            <CardDescription className="line-clamp-3 text-sm text-slate-600">
              <div dangerouslySetInnerHTML={{ __html: post.excerpt || "" }} />
            </CardDescription>
          </CardContent>
          <CardFooter className="p-5 pt-0">
            <Link to={post.link || "#"} className="group flex items-center text-sm font-bold uppercase tracking-tight text-primary">
              Leggi Articolo <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}


function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <div className="mb-8 h-12 w-3/4 animate-pulse rounded-none bg-slate-100" />
        <div className="mb-8 h-96 w-full animate-pulse rounded-none bg-slate-100" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
          <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
          <div className="h-4 w-5/6 animate-pulse rounded bg-slate-100" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
        <h2 className="mb-4 text-2xl font-bold text-slate-900">Impossibile caricare l'articolo</h2>
        <p className="mb-6 text-slate-600">Si è verificato un errore durante il recupero del contenuto tecnico.</p>
        <Button onClick={refetch} className="rounded-none px-8">Riprova</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
        <h2 className="mb-4 text-2xl font-bold text-slate-900">Articolo non trovato</h2>
        <p className="mb-6 text-slate-600">L'approfondimento che stai cercando non esiste o è stato rimosso dall'archivio.</p>
        <Link to="/HomePage">
          <Button className="rounded-none px-8">Torna alla Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  
  
  const relatedQuery = new WP_Query({
    post_type: "post",
    posts_per_page: 3,
    post__not_in: [post.id],
    orderby: "date",
    order: "DESC",
  });

  return (
    <article className="min-h-screen bg-white pb-20">
      {}
      <div className="bg-slate-50/80 py-12 border-b border-slate-100 md:py-24">
        <div className="container mx-auto max-w-4xl px-4">
          {}
          {post.categories && post.categories.length > 0 && (
            <div className="mb-8 flex flex-wrap gap-2">
              {post.categories.map((cat, i) => (
                <span
                  key={cat.id}
                  className="inline-flex items-center rounded-none bg-primary px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white"
                  data-index={i}
                >
                  {cat.name}
                </span>
              ))}
            </div>
          )}

          {}
          <h1 className="mb-8 text-4xl font-extrabold leading-tight tracking-tight text-slate-900 md:text-5xl lg:text-6xl">
            {post.title}
          </h1>

          {}
          <div className="flex flex-wrap items-center gap-8 text-sm font-medium text-slate-500">
            {post.author && (
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 border-2 border-white shadow-sm">
                  <AvatarImage src={post.author.avatar?.["96"] || ""} alt={post.author.name} />
                  <AvatarFallback className="bg-slate-200"><User className="h-5 w-5 text-slate-600" /></AvatarFallback>
                </Avatar>
                <span className="font-bold text-slate-900">{post.author.name}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              <span>{post.date ? new Date(post.date).toLocaleDateString('it-IT', { year: 'numeric', month: 'long', day: 'numeric' }) : ""}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              <span>5 min di lettura</span>
            </div>
          </div>
        </div>
      </div>

      {}
      {post.featuredImage && (
        <div className="container mx-auto -mt-10 max-w-5xl px-4 md:-mt-16">
          <div className="overflow-hidden rounded-none border-4 border-white bg-white shadow-2xl">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-auto w-full object-cover"
            />
          </div>
        </div>
      )}

      {}
      <div className="container mx-auto mt-16 max-w-3xl px-4">
        <div 
          className="prose prose-xl prose-slate max-w-none dark:prose-invert 
            [&_h2]:mt-12 [&_h2]:mb-6 [&_h2]:text-3xl [&_h2]:font-extrabold [&_h2]:text-slate-900 [&_h2]:border-l-4 [&_h2]:border-primary [&_h2]:pl-4
            [&_h3]:mt-10 [&_h3]:mb-4 [&_h3]:text-2xl [&_h3]:font-bold [&_h3]:text-slate-800
            [&_p]:mb-8 [&_p]:leading-relaxed [&_p]:text-slate-700
            [&_ul]:mb-8 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:mb-3 [&_li]:text-slate-700
            [&_ol]:mb-8 [&_ol]:list-decimal [&_ol]:pl-6
            [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:bg-slate-50 [&_blockquote]:p-8 [&_blockquote]:italic [&_blockquote]:text-slate-800 [&_blockquote]:font-medium [&_blockquote]:rounded-none
            [&_img]:rounded-none [&_img]:shadow-xl [&_img]:my-12 [&_img]:border [&_img]:border-slate-100
            [&_a]:text-primary [&_a]:font-bold [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-slate-700"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />

        {}
        <div className="mt-16 flex flex-col gap-6 border-t-2 border-slate-100 pt-10 md:flex-row md:items-center md:justify-between">
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              <Tag className="h-4 w-4 text-slate-400" />
              {post.tags.map((tag, i) => (
                <span key={tag.id} className="text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-primary transition-colors cursor-pointer" data-index={i}>
                  #{tag.name}
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center gap-4">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-900">Condividi:</span>
            <Button variant="outline" size="icon" className="h-10 w-10 rounded-none border-slate-200 hover:bg-primary hover:text-white transition-all">
              <Share2 className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {}
        {post.author && (
          <div className="mt-16 rounded-none border border-slate-200 bg-slate-50 p-10">
            <div className="flex flex-col gap-8 sm:flex-row sm:items-start">
              <Avatar className="h-20 w-20 border-4 border-white shadow-md">
                <AvatarImage src={post.author.avatar?.["96"] || ""} alt={post.author.name} />
                <AvatarFallback className="text-2xl bg-slate-200"><User /></AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="text-xl font-extrabold text-slate-900">{post.author.name}</h3>
                <p className="mt-3 text-base leading-relaxed text-slate-600">
                  {post.author.description || `Consulente tecnico esperto presso Studio MESS S.r.l. Specializzato in sicurezza sul lavoro, progettazione antincendio e normativa tecnica D.Lgs 81/08.`}
                </p>
                {post.author.link && (
                  <Link to={post.author.link} className="mt-4 inline-flex items-center text-sm font-bold text-primary hover:text-slate-900 transition-colors">
                    Vedi tutti gli articoli di {post.author.name} <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {}
      <div className="mt-24 bg-slate-900 py-20">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="mb-12 flex items-center justify-between">
            <h2 className="text-3xl font-extrabold tracking-tight text-white md:text-4xl">Articoli Correlati</h2>
            <Link to="/NewsRegulationsPage">
              <Button variant="outline" className="hidden sm:flex rounded-none border-white/20 text-white hover:bg-white hover:text-slate-900">
                Vedi Tutti gli Articoli <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          
          <PostsProvider wp_query={relatedQuery}>
            <RelatedPostsConsumer />
          </PostsProvider>
          
          <div className="mt-12 flex justify-center sm:hidden">
             <Link to="/NewsRegulationsPage">
              <Button variant="outline" className="rounded-none border-white/20 text-white">Vedi Tutti gli Articoli</Button>
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}


export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: 'post' }; 

  
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="w-full bg-white text-slate-900">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}